import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, ArrowDownRight, ArrowRight, ArrowUpRight, DollarSign, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  getAnalysisMetricsTrends,
  type AnalysisMetricsTrends,
  type MetricsTrendDirection,
} from '@/services/adminService';

function formatDuration(ms: number | undefined): string {
  if (ms == null || Number.isNaN(ms)) return '—';
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}m ${sec}s`;
}

function formatCost(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `$${value.toFixed(2)}`;
}

function TrendBadge({ direction, label }: { direction: MetricsTrendDirection; label: string }) {
  const Icon =
    direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : ArrowRight;
  const tone =
    direction === 'up'
      ? 'text-amber-300 border-amber-500/30 bg-amber-500/10'
      : direction === 'down'
        ? 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10'
        : 'text-white/55 border-white/15 bg-white/5';

  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-mono', tone)}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

function MiniBarChart({
  rows,
  valueKey,
  maxBars = 14,
}: {
  rows: AnalysisMetricsTrends['daily'];
  valueKey: 'avg_cost' | 'avg_duration_ms';
  maxBars?: number;
}) {
  const slice = rows.slice(-maxBars);
  const max = Math.max(...slice.map((r) => Number(r[valueKey] ?? 0)), 0.0001);

  return (
    <div className="flex items-end gap-1.5 h-28">
      {slice.map((row) => {
        const val = Number(row[valueKey] ?? 0);
        const height = Math.max(6, Math.round((val / max) * 100));
        return (
          <div key={row.date} className="flex-1 min-w-0 flex flex-col items-center gap-1">
            <div
              className="w-full rounded-sm bg-index-accent/70"
              style={{ height: `${height}%` }}
              title={`${row.date}: ${valueKey === 'avg_cost' ? formatCost(val) : formatDuration(val)}`}
            />
            <span className="text-[9px] font-mono text-white/35 truncate w-full text-center">
              {row.date.slice(5)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function AnalysisInstrumentationPanel() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalysisMetricsTrends | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getAnalysisMetricsTrends({ days, limit: 200 }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const costTrendCopy = useMemo(() => {
    if (!data) return '';
    const { trends } = data;
    return `${formatCost(trends.recent_avg_cost_est)} recent vs ${formatCost(trends.prior_avg_cost_est)} prior`;
  }, [data]);

  const durationTrendCopy = useMemo(() => {
    if (!data) return '';
    const { trends } = data;
    return `${formatDuration(trends.recent_avg_duration_ms)} recent vs ${formatDuration(trends.prior_avg_duration_ms)} prior`;
  }, [data]);

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-mono text-white/45 max-w-2xl">
          Legacy V7 cost/duration store (all users). Methodology runs appear under Step Analysis
          and ML metrics until this store is wired to the new pipeline.
        </p>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              variant={days === d ? 'indexPrimary' : 'indexOutline'}
              size="sm"
              onClick={() => setDays(d)}
            >
              {d}d
            </Button>
          ))}
          <Button variant="indexOutline" size="sm" onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {loading && (
        <div className="ni-panel py-16 text-center text-white/45 font-sans">Loading metrics…</div>
      )}

      {error && (
        <div className="mb-6 ni-panel border-red-500/30 px-4 py-3 text-red-300 text-sm">{error}</div>
      )}

      {!loading && data && (
        <>
          <section className="grid gap-4 md:grid-cols-3 mb-6">
            <div className="ni-panel p-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="ni-label">Avg cost / run</p>
                <DollarSign className="h-4 w-4 text-white/35" />
              </div>
              <p className="text-2xl font-sans font-semibold text-white">
                {formatCost(data.summary.avg_cost_est)}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <TrendBadge direction={data.trends.cost_direction} label="Cost trend" />
                <span className="text-xs text-white/45 font-mono">{costTrendCopy}</span>
              </div>
            </div>
            <div className="ni-panel p-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="ni-label">Avg duration / run</p>
                <Timer className="h-4 w-4 text-white/35" />
              </div>
              <p className="text-2xl font-sans font-semibold text-white">
                {formatDuration(data.summary.avg_duration_ms)}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <TrendBadge direction={data.trends.duration_direction} label="Duration trend" />
                <span className="text-xs text-white/45 font-mono">{durationTrendCopy}</span>
              </div>
            </div>
            <div className="ni-panel p-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="ni-label">Runs captured</p>
                <Activity className="h-4 w-4 text-white/35" />
              </div>
              <p className="text-2xl font-sans font-semibold text-white">{data.summary.run_count}</p>
              <p className="mt-3 text-xs text-white/45 font-mono">
                {data.summary.completed_count} completed in selected window
              </p>
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-2 mb-6">
            <div className="ni-panel p-5">
              <p className="ni-label mb-4">Daily avg cost</p>
              <MiniBarChart rows={data.daily} valueKey="avg_cost" />
            </div>
            <div className="ni-panel p-5">
              <p className="ni-label mb-4">Daily avg duration</p>
              <MiniBarChart rows={data.daily} valueKey="avg_duration_ms" />
            </div>
          </section>

          <section className="ni-panel overflow-hidden">
            <div className="px-5 py-4 border-b border-[#2a2a2a]">
              <p className="ni-label">Recent runs</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-sans">
                <thead>
                  <tr className="text-left text-white/45 border-b border-[#2a2a2a]">
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Ended</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">User</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Cost</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Duration</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Pages</th>
                  </tr>
                </thead>
                <tbody>
                  {data.runs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-center text-white/45">
                        No runs recorded yet. Metrics appear after the next analyses complete.
                      </td>
                    </tr>
                  )}
                  {data.runs.map((run) => (
                    <tr key={run.run_id ?? run.session_id} className="border-b border-[#1f1f1f] text-white/80">
                      <td className="px-5 py-3 font-mono text-xs whitespace-nowrap">
                        {(run.ended_at || run.started_at || '—').slice(0, 19).replace('T', ' ')}
                      </td>
                      <td className="px-5 py-3 text-xs truncate max-w-[180px]">{run.user_id || '—'}</td>
                      <td className="px-5 py-3 capitalize">{run.status || '—'}</td>
                      <td className="px-5 py-3">{formatCost(run.total_cost_est)}</td>
                      <td className="px-5 py-3">{formatDuration(run.total_wall_clock_ms)}</td>
                      <td className="px-5 py-3">{run.script_page_count ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="ni-panel overflow-hidden mt-6">
            <div className="px-5 py-4 border-b border-[#2a2a2a]">
              <p className="ni-label">Per agent breakdown</p>
              <p className="mt-1 text-xs font-mono text-white/40">
                Average cost and duration per pipeline stage with recent-vs-prior trend.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-sans">
                <thead>
                  <tr className="text-left text-white/45 border-b border-[#2a2a2a]">
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Agent</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Avg cost</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Cost trend</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Avg duration</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Duration trend</th>
                    <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Runs</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.agents ?? []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-center text-white/45">
                        No per-agent stage metrics yet. Stage data is recorded on each completed analysis.
                      </td>
                    </tr>
                  )}
                  {(data.agents ?? []).map((agent) => (
                    <tr
                      key={agent.phase_name}
                      className="border-b border-[#1f1f1f] text-white/80"
                    >
                      <td className="px-5 py-3">
                        <span className="block text-sm">{agent.label}</span>
                        <span className="block text-[10px] font-mono text-white/35 mt-0.5">
                          {agent.phase_name}
                        </span>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">{formatCost(agent.avg_cost_est)}</td>
                      <td className="px-5 py-3">
                        <div className="flex flex-col gap-1.5 items-start">
                          <TrendBadge direction={agent.cost_direction} label="Cost" />
                          <span className="text-[10px] font-mono text-white/40">
                            {formatCost(agent.recent_avg_cost_est)} recent vs{' '}
                            {formatCost(agent.prior_avg_cost_est)} prior
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {formatDuration(agent.avg_duration_ms)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-col gap-1.5 items-start">
                          <TrendBadge direction={agent.duration_direction} label="Duration" />
                          <span className="text-[10px] font-mono text-white/40">
                            {formatDuration(agent.recent_avg_duration_ms)} recent vs{' '}
                            {formatDuration(agent.prior_avg_duration_ms)} prior
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 font-mono text-xs">{agent.run_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
